import { VariantType, useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { TabModules, useModulesConfig } from '~/data/company';
import { DevelopmentTabs, PeopleTabs, RewardingTabs } from '~/data/tabs';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { ApprovalWidgetProps } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/component';
import {
  ApprovalWidgetCategory,
  BulkApprovalAction,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import { ApprovalWidgetData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import { useApprovalWidgetAbsencesData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/useApprovalWidgetAbsencesData';
import {
  ApprovalWidgetRewardingDataDetailedState,
  useApprovalWidgetRewardingData,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/useApprovalWidgetRewardingData';
import { useApprovalWidgetTrainingData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/useApprovalWidgetTrainingData';
import { getWidgetDataRequestRoles } from '~/scenes/MyWorkspace/components/Widgets/data';

export type ApprovalWidgetDataDetailedState =
  ApprovalWidgetRewardingDataDetailedState & {
    absences: boolean;
    training: boolean;
  };

export type ApprovalWidgetDataSets = {
  absences: ApprovalWidgetData[];
  training: ApprovalWidgetData[];
  allowance: ApprovalWidgetData[];
  benefit: ApprovalWidgetData[];
  incentive: ApprovalWidgetData[];
  salary: ApprovalWidgetData[];
};

export type UseApprovalWidgetDataOutput = {
  isLoading: boolean;
  isLoadingDetailed: ApprovalWidgetDataDetailedState;
  failedToLoad: boolean;
  failedToLoadDetailed: ApprovalWidgetDataDetailedState;
  data: ApprovalWidgetDataSets;
  onDecision: (
    action: BulkApprovalAction,
    category: ApprovalWidgetCategory,
  ) => Promise<void>;
  selection: ApprovalWidgetDataSets;
  setSelection: (
    category: ApprovalWidgetCategory,
    selection: ApprovalWidgetData[],
  ) => void;
  enabledSections: {
    absences: boolean;
    rewarding: boolean;
    training: boolean;
  };
};

export const useApprovalWidgetData = (
  config: ApprovalWidgetProps['config'],
): UseApprovalWidgetDataOutput => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { isModuleTabEnabled } = useModulesConfig();

  const employeeId = useLoggedInEmployeeId();
  const userRoles = useLoggedInUserRoles();

  const {
    dataRequestRoles,
    isRewardingEnabled,
    isTrainingsEnabled,
    isAbsencesEnabled,
  } = useMemo(() => {
    const dataRequestRoles = getWidgetDataRequestRoles(config?.role);

    const hasRewardingAccess =
      config?.role !== AdminWorkspaceRoles.DEPUTY_MANAGER;

    return {
      dataRequestRoles,
      isAbsencesEnabled: isModuleTabEnabled(
        TabModules.PEOPLE,
        PeopleTabs.Absences,
      ),
      isTrainingsEnabled: isModuleTabEnabled(
        TabModules.DEVELOPMENT,
        DevelopmentTabs.Education,
      ),
      isRewardingEnabled:
        hasRewardingAccess &&
        isModuleTabEnabled(TabModules.REWARDING, RewardingTabs.General),
    };
  }, [config?.role, isModuleTabEnabled]);

  const showSnackBar = useCallback(
    (
      responsesNumber: number,
      variant: VariantType,
      action: BulkApprovalAction,
    ) => {
      enqueueSnackbar(
        `${responsesNumber}
        ${responsesNumber === 1 ? t('itemWas') : t('itemsWere')}
        ${variant === 'error' ? t('not') : ''}
      ${
        action === BulkApprovalAction.APPROVE
          ? t('approved').toLowerCase()
          : t('rejected').toLowerCase()
      }
      ${variant === 'success' ? t('successfully').toLowerCase() : ''}`,
        {
          variant,
        },
      );
    },
    [enqueueSnackbar, t],
  );

  const swrConfig: SWRConfiguration = {
    refreshInterval: 0,
  };

  const absencesData = useApprovalWidgetAbsencesData(
    {
      employeeId,
      userRoles,
      widgetRoles: dataRequestRoles,
      showSnackBar,
      enabled: isAbsencesEnabled,
    },
    swrConfig,
  );
  const trainingData = useApprovalWidgetTrainingData(
    {
      widgetRoles: dataRequestRoles,
      showSnackBar,
      enabled: isTrainingsEnabled,
    },
    swrConfig,
  );
  const rewardingData = useApprovalWidgetRewardingData(
    {
      employeeId,
      userRoles,
      showSnackBar,
      enabled: isRewardingEnabled,
      widgetRoles: dataRequestRoles,
    },
    swrConfig,
  );

  const {
    onDecision: onRewardingDecision,
    setSelection: setRewardingSelection,
  } = rewardingData;
  const { onDecision: onAbsencesDecision, setSelection: setAbsencesSelection } =
    absencesData;
  const { onDecision: onTrainingDecision, setSelection: setTrainingSelection } =
    trainingData;

  const handleDecision = useCallback(
    (
      action: BulkApprovalAction,
      category: ApprovalWidgetCategory,
    ): Promise<void> => {
      switch (category) {
        case ApprovalWidgetCategory.Absences:
          return onAbsencesDecision(action);
        case ApprovalWidgetCategory.Allowance:
        case ApprovalWidgetCategory.Benefit:
        case ApprovalWidgetCategory.Incentive:
        case ApprovalWidgetCategory.Salary:
          return onRewardingDecision(action, category);
        case ApprovalWidgetCategory.Training:
          return onTrainingDecision(action);
        default:
          return Promise.resolve();
      }
    },
    [onRewardingDecision, onAbsencesDecision, onTrainingDecision],
  );

  const handleSelection = useCallback(
    (category: ApprovalWidgetCategory, selection: ApprovalWidgetData[]) => {
      switch (category) {
        case ApprovalWidgetCategory.Absences:
          return setAbsencesSelection(selection);
        case ApprovalWidgetCategory.Allowance:
        case ApprovalWidgetCategory.Benefit:
        case ApprovalWidgetCategory.Incentive:
        case ApprovalWidgetCategory.Salary:
          return setRewardingSelection(category, selection);
        case ApprovalWidgetCategory.Training:
          return setTrainingSelection(selection);
        default:
          return;
      }
    },
    [setAbsencesSelection, setRewardingSelection, setTrainingSelection],
  );

  return {
    isLoading:
      absencesData.isLoading ||
      trainingData.isLoading ||
      rewardingData.isLoading,
    isLoadingDetailed: {
      absences: absencesData.isLoading,
      training: trainingData.isLoading,
      ...rewardingData.isLoadingDetailed,
    },
    failedToLoad:
      absencesData.failedToLoad ||
      trainingData.failedToLoad ||
      rewardingData.failedToLoad,
    failedToLoadDetailed: {
      absences: absencesData.failedToLoad,
      training: trainingData.failedToLoad,
      ...rewardingData.failedToLoadDetailed,
    },
    data: {
      ...absencesData.data,
      ...trainingData.data,
      ...rewardingData.data,
    },
    onDecision: handleDecision,
    selection: {
      absences: absencesData.selection,
      training: trainingData.selection,
      ...rewardingData.selection,
    },
    setSelection: handleSelection,
    enabledSections: {
      absences: isAbsencesEnabled,
      rewarding: isRewardingEnabled,
      training: isTrainingsEnabled,
    },
  };
};
